import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  ErrorAlert,
  useToast,
  Badge,
  Modal,
  KeyValue,
  Button,
  Section,
  type Requirement,
} from "@/components/ui";
import {
  daysUntil,
  domainKeys,
  domainsApi,
  has,
  longDate,
  useDomainDetail,
  verificationApplies,
  type DomainDetail,
  type TransferRecord,
} from "@/lib/domains";

import { pendingOp, shownError, useConfirmed } from "../security/shared";
import { TransferModal } from "../TransferModal";
import { TwoColumns, Notice, Hint, RuleRow } from "@/components/domains/parts";

const STEPS = [
  "Unlock the domain for transfer",
  "Get your authorization code (EPP code)",
  "Enter that code with your new registrar",
  "Approve the transfer request when it arrives",
];

/** Figma "Domain Control Centre — Transfers" (2046-4) and "Confirm it's you" for the code (4108-1281). */
export function TransfersTab({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId, canOn } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const { run, modal } = useConfirmed();
  const [code, setCode] = useState<{ code: string | null; sent_to: string | null } | null>(null);
  const manage = canOn("domains:manage", "domain_folder", detail.folder?.id);
  const requires: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };

  // The shell's "Transfer with Code" button (?modal=transfer-in) and "Start a transfer in" open the same modal here.
  const transferOpen = params.get("modal") === "transfer-in";
  const setTransferOpen = (open: boolean) =>
    setParams(
      (p) => {
        if (open) p.set("modal", "transfer-in");
        else p.delete("modal");
        return p;
      },
      { replace: true },
    );

  const lockOp = pendingOp(detail, "transfer_lock");
  useDomainDetail(d.id, { poll: Boolean(lockOp) });
  const lockReason = lockOp ? "Waiting for the registry to confirm the last change" : undefined;

  const refresh = () => qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  const lock = useMutation({
    mutationFn: (locked: boolean) =>
      run(() => domainsApi.setTransferLock(orgId, d.id, locked), {
        subtitle: `Unlocking lets ${d.display_name} move to another registrar — we verify identity first`,
        label: "Verify & unlock",
      }),
    onSuccess: async (_, locked) => {
      await refresh();
      toast.show({ tone: "success", title: locked ? "Transfer lock turned on" : "Transfer lock turned off" });
    },
  });
  const emailed = has(detail, "send_auth_code");
  const authCode = useMutation({
    mutationFn: () =>
      run(() => domainsApi.authCode(orgId, d.id, emailed ? "email" : "show"), {
        subtitle: `This code can move ${d.display_name} to another registrar — we verify identity first`,
        label: emailed ? "Verify & send code" : "Verify & show code",
        hint: (
          <Hint tone="warning" title="Keep this code private">
            Anyone with this code can transfer this domain away from CortexGrip.
            {emailed
              ? ` We'll email it to ${detail.contact?.email ?? "the registrant"} rather than show it here.`
              : " It's shown once — copy it straight into your new registrar."}
          </Hint>
        ),
      }),
    onSuccess: (result) => setCode(result),
  });

  const canLock = has(detail, "transfer_lock");
  const canCode = emailed || has(detail, "auth_code");
  const windowOpen = d.transfer_window_open;
  const registeredDays = daysUntil(d.registered_at ?? d.created_at);
  const registeredAgo = registeredDays === null ? null : Math.max(0, -registeredDays);
  const verified = verificationApplies(detail.contact, d.fqdn)
    ? detail.contact?.verification
    : "not_required";
  const dnsHere = d.nameserver_mode === "cortexgrip";

  return (
    <>
      <TwoColumns
        main={
          <>
            <Section
              title="Transfer out"
              subtitle={`Move ${d.display_name} to a different registrar`}
              action={
                lockOp ? (
                  <Badge size="md" tone="warning">
                    Updating
                  </Badge>
                ) : (
                  <Badge size="md" tone={d.transfer_lock ? "neutral" : "warning"}>
                    {d.transfer_lock ? "Locked" : "Unlocked"}
                  </Badge>
                )
              }
            >
              <p className="max-w-[640px] text-sm text-fg-subtle">
                {!windowOpen
                  ? "Registries don't allow transfers in the 60 days after a registration or transfer, so this domain can't move yet. You can still get the authorization code ready."
                  : d.transfer_lock
                    ? "Transfer lock is currently on, which blocks outbound transfers. This protects against unauthorized moves — unlock it below only when you're ready to transfer."
                    : "Transfer lock is off, so your new registrar can request this domain with the authorization code. Turn the lock back on if you change your mind."}
              </p>
              <ol className="flex flex-col gap-2.5">
                {STEPS.map((s, i) => (
                  <li key={s} className="flex items-center gap-2.5 text-sm text-fg">
                    <span className="flex size-[18px] shrink-0 items-center justify-center rounded-[5px] bg-[#f3ebe7] text-[11px] font-semibold text-[#5c5551]">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
              <div className="flex flex-wrap gap-2.5">
                {canLock &&
                  (d.transfer_lock ? (
                    <Button
                      variant="brand"
                      size="lg"
                      requires={requires}
                      dispute
                      loading={lock.isPending}
                      disabledReason={
                        lockReason ??
                        (windowOpen
                          ? undefined
                          : "Registries don't allow transfers in the 60 days after a registration or transfer")
                      }
                      onClick={() => lock.mutate(false)}
                    >
                      Unlock for transfer
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      variant="secondary"
                      requires={requires}
                      dispute
                      loading={lock.isPending}
                      disabledReason={lockReason}
                      onClick={() => lock.mutate(true)}
                    >
                      Turn transfer lock back on
                    </Button>
                  ))}
                {canCode && (
                  <Button
                    size="lg"
                    variant="secondary"
                    requires={requires}
                    dispute
                    loading={authCode.isPending}
                    onClick={() => authCode.mutate()}
                  >
                    Get authorization code
                  </Button>
                )}
              </div>
              {!canCode && (
                <p className="text-xs text-fg-subtle">
                  The .{d.tld} registry doesn't hand out authorization codes through CortexGrip — contact
                  support to transfer this domain away.
                </p>
              )}
              <ErrorAlert error={shownError(lock.error) ?? shownError(authCode.error)} />
            </Section>

            <Section
              title="Transfer in a domain you own elsewhere"
              subtitle="Bring a domain from another registrar into CortexGrip"
              action={
                <Button
                  size="lg"
                  variant="secondary"
                  requires={requires}
                  onClick={() => setTransferOpen(true)}
                >
                  Start a transfer in
                </Button>
              }
            >
              <p className="text-sm text-fg-subtle">
                Moving several domains?{" "}
                <Link to="/domains/transfer/bulk" className="font-semibold text-brand-500 hover:underline">
                  Transfer up to 100 at once
                </Link>
              </p>
            </Section>

            <TransferHistory domainId={d.id} />
          </>
        }
        side={
          <>
            <Section title="Transfer eligibility">
              <KeyValue variant="detail" label="Registered">
                <Badge size="md" tone="success">
                  {registeredAgo === null
                    ? "—"
                    : registeredAgo === 0
                      ? "Today"
                      : `${registeredAgo} ${registeredAgo === 1 ? "day" : "days"} ago`}
                </Badge>
              </KeyValue>
              <KeyValue variant="detail" label="60-day lock after registration">
                <Badge size="md" tone={windowOpen ? "success" : "warning"}>
                  {windowOpen ? "Passed" : "Not yet"}
                </Badge>
              </KeyValue>
              <KeyValue variant="detail" label="Transfer lock">
                <Badge size="md" tone={lockOp ? "warning" : "neutral"}>
                  {lockOp ? "Updating" : d.transfer_lock ? "On" : "Off"}
                </Badge>
              </KeyValue>
              <KeyValue variant="detail" label="Registrant verified">
                {!detail.contact ? (
                  <Badge size="md" tone="neutral">
                    No registrant
                  </Badge>
                ) : verified === "not_required" ? (
                  <Badge size="md" tone="neutral">
                    Not required
                  </Badge>
                ) : (
                  <Badge size="md" tone={verified === "verified" ? "success" : "warning"}>
                    {verified === "verified" ? "Yes" : "Pending"}
                  </Badge>
                )}
              </KeyValue>
            </Section>
            <Notice tone="warning" title="Before you transfer out">
              {!dnsHere
                ? "This domain already uses another DNS provider, so moving registrars won't change where it points. Keep those nameservers in place during the move."
                : d.site
                  ? `Your website ${d.site.name} on this domain depends on CortexGrip DNS. Moving registrars doesn't move DNS automatically — plan the DNS cutover with your new provider first.`
                  : "This domain's DNS is hosted by CortexGrip. Moving registrars doesn't move DNS automatically — copy your records to your new provider first."}
            </Notice>
          </>
        }
      />
      <AuthCodeModal domain={d.display_name} result={code} onClose={() => setCode(null)} />
      {manage && (
        <TransferModal
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          onAdded={(fqdn) =>
            toast.show({
              tone: "success",
              title: `${fqdn} added to your cart`,
              body: "Check out to start the transfer.",
              duration: 12_000,
              action: { label: "Go to checkout", onClick: () => navigate("/domains/checkout") },
            })
          }
        />
      )}
      {modal}
    </>
  );
}

const STATUS: Record<string, { label: string; tone: "success" | "warning" | "danger" | "neutral" }> = {
  in_progress: { label: "In progress", tone: "warning" },
  pending: { label: "Waiting for an answer", tone: "warning" },
  completed: { label: "Completed", tone: "success" },
  approved: { label: "Approved", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

function describe(t: TransferRecord): string {
  const where = t.direction === "in" ? "Transfer in to CortexGrip" : "Transfer away to another registrar";
  if (t.direction === "out" && t.status === "pending" && t.auto_completes_at)
    return `${where} · goes through on ${longDate(t.auto_completes_at)} unless rejected`;
  if (t.answered_via)
    return `${where} · answered ${t.answered_via === "email" ? "by e-mail" : "in the portal"}`;
  return where;
}

/** Every transfer of this domain, in and away, with its final status and date (PHP-3941). */
function TransferHistory({ domainId }: { domainId: string }) {
  const { orgId } = useOrganization();
  const history = useQuery({
    queryKey: domainKeys.transfers(orgId, domainId),
    queryFn: () => domainsApi.transfers(orgId, domainId),
  });
  const rows = history.data?.transfers ?? [];
  return (
    <Section title="Transfer history" subtitle="Every transfer of this domain, in and away">
      <ErrorAlert error={history.error} />
      {history.isSuccess && rows.length === 0 && <p className="text-sm text-fg-subtle">No transfers yet.</p>}
      {rows.length > 0 && (
        <ul aria-label="Transfer history" className="flex flex-col gap-3.5">
          {rows.map((t, i) => {
            const status = STATUS[t.status] ?? { label: t.status, tone: "neutral" as const };
            return (
              <li key={`${t.direction}-${t.started_at}-${i}`}>
                <RuleRow first={i === 0}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-fg">{describe(t)}</p>
                    <p className="text-xs text-fg-subtle">
                      {longDate(t.ended_at ?? t.started_at)}
                      {t.reason ? ` · ${t.reason}` : ""}
                      {t.order_id && (
                        <>
                          {" · "}
                          <Link
                            to={`/domains/orders/${t.order_id}`}
                            className="text-brand-500 hover:underline"
                          >
                            View order
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                  <Badge size="md" tone={status.tone}>
                    {status.label}
                  </Badge>
                </RuleRow>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function AuthCodeModal({
  domain,
  result,
  onClose,
}: {
  domain: string;
  result: { code: string | null; sent_to: string | null } | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = (value: string) => {
    void navigator.clipboard.writeText(value).then(() => setCopied(true));
  };
  return (
    <Modal
      open={result !== null}
      onClose={() => {
        setCopied(false);
        onClose();
      }}
      title="Authorization code"
      subtitle={`For transferring ${domain} to another registrar`}
      footer={
        <Button
          variant="brand"
          size="lg"
          onClick={() => {
            setCopied(false);
            onClose();
          }}
        >
          Done
        </Button>
      }
    >
      {result?.code ? (
        <>
          <div className="flex items-center gap-2 rounded-xl bg-[#faf6ef] p-3">
            <code className="min-w-0 flex-1 font-mono text-sm break-all text-fg">{result.code}</code>
            <Button
              unstyled
              type="button"
              aria-label="Copy authorization code"
              onClick={() => copy(result.code!)}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface text-fg-muted hover:text-fg"
            >
              {copied ? <Check className="size-4 text-[#007955]" /> : <Copy className="size-4" />}
            </Button>
          </div>
          <Hint tone="warning" title="Shown once — keep it private">
            Anyone with this code can transfer {domain} away from CortexGrip. Paste it into your new registrar
            now; you can get a new code here any time.
          </Hint>
        </>
      ) : (
        <Hint tone="success" title="Code sent">
          We've emailed the code to {result?.sent_to ?? "the registrant's email address"}. It can take a few
          minutes to arrive — enter it with your new registrar.
        </Hint>
      )}
    </Modal>
  );
}
